"use client";
import { useStyleContext } from "@gluestack-ui/utils/nativewind-utils";
import type { ComponentProps, ComponentRef } from "react";
import React, { useCallback } from "react";
import { Box } from "../box";
import { Slider, SliderThumb, SliderTrack } from "../slider";
import {
  discreteSliderStepStyle,
  discreteSliderStyle,
  discreteSliderThumbStyle,
  discreteSliderTrackStyle,
} from "./styles";

interface DiscreteSliderProps extends Omit<
  ComponentProps<typeof Slider>,
  "onChange"
> {
  steps: number;
  onChange?: (value: number) => void;
}

const DiscreteSliderStep = (props: { index: number; steps: number }) => {
  const { index, steps } = props;
  const { size: parentSize } = useStyleContext("SLIDER");
  return (
    <Box
      key={index}
      className={discreteSliderStepStyle({
        parentVariants: {
          size: parentSize,
        },
      })}
      style={{
        left: `${(index / steps) * 100}%`,
      }}
    />
  );
};

export const DiscreteSlider = React.forwardRef<
  ComponentRef<typeof Slider>,
  DiscreteSliderProps
>(
  (
    {
      steps,
      onChange,
      size = "md",
      orientation = "horizontal",
      minValue = 0,
      maxValue = 1,
      ...props
    },
    ref,
  ) => {
    const range = maxValue - minValue;
    const stepSize = range / steps;
    const handleChange = useCallback(
      (value: number) => {
        // Calcular el paso más cercano
        const nearestStep =
          Math.round((value - minValue) / stepSize) * stepSize;
        onChange?.(nearestStep + minValue);
      },
      [onChange, stepSize, minValue],
    );

    return (
      <Slider
        ref={ref}
        onChange={handleChange}
        className={discreteSliderStyle({ size, orientation })}
        minValue={minValue}
        maxValue={maxValue}
        size={size}
        {...props}
      >
        {Array.from({ length: steps + 1 }).map((_, index) => {
          return <DiscreteSliderStep key={index} index={index} steps={steps} />;
        })}
        <SliderTrack className={discreteSliderTrackStyle({ orientation })} />
        <SliderThumb className={discreteSliderThumbStyle({ size })} />
      </Slider>
    );
  },
);

DiscreteSlider.displayName = "DiscreteSlider";
